import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import {
  BbTask,
  bbCreateTask,
  bbGetTasks,
  bbUpdateTask,
  BbXpRule,
  bbUpsertXpRule,
  bbGetXpRules,
} from '../../../services/blindBoxService';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import { PermissionData } from '../../../entities/permission.entity';
import { hasPermission } from '../../../utils/has-permission';
import { TIER_SLUG_TOP_XP_ACTION, TIER_SLUGS } from '../shared/tierSlugs';

// /blind-box/tasks routes require bb-tasks:<action> OR bb-trading-tiers:<action>
const TASK_CREATE_PERMS = [
  { resource: 'bb-tasks', action: 'create' },
  { resource: 'bb-trading-tiers', action: 'create' },
];
const TASK_UPDATE_PERMS = [
  { resource: 'bb-tasks', action: 'update' },
  { resource: 'bb-trading-tiers', action: 'update' },
];

const TASK_SLUG_I18N: Record<string, string> = {
  NEWBIE_REGISTER: 'module.blindBox.taskSlug_NEWBIE_REGISTER',
  NEWBIE_FIRST_DEPOSIT: 'module.blindBox.taskSlug_NEWBIE_FIRST_DEPOSIT',
  NEWBIE_FIRST_TRADE: 'module.blindBox.taskSlug_NEWBIE_FIRST_TRADE',
  DAILY_LOGIN: 'module.blindBox.taskSlug_DAILY_LOGIN',
  DAILY_TRADING_FUTURES: 'module.blindBox.taskSlug_DAILY_TRADING_FUTURES',
  DAILY_TRADING_COPY: 'module.blindBox.taskSlug_DAILY_TRADING_COPY',
  DAILY_TRADING_PREDICTING: 'module.blindBox.taskSlug_DAILY_TRADING_PREDICTING',
  DAILY_TRADING_PREDICTION_LEVERAGE: 'module.blindBox.taskSlug_DAILY_TRADING_PREDICTION_LEVERAGE',
  DAILY_TRADING_TAP_GRID: 'module.blindBox.taskSlug_DAILY_TRADING_TAP_GRID',
  SOCIAL_TWITTER_FOLLOW: 'module.blindBox.taskSlug_SOCIAL_TWITTER_FOLLOW',
  SOCIAL_TELEGRAM_FOLLOW: 'module.blindBox.taskSlug_SOCIAL_TELEGRAM_FOLLOW',
};

/** 后端支持的 slug 全集（含交易量阶梯任务），新增任务时作为候选项 */
const KNOWN_TASK_SLUGS = [
  ...Object.keys(TASK_SLUG_I18N).filter((s) => !TIER_SLUGS.includes(s)),
  ...TIER_SLUGS,
  'SOCIAL_INVITE_FRIEND',
];

const XP_FILTER_AND_ORDER = [
  'TRADE',
  'BOX_OPEN',
  'DAILY_PERP_TOP',
  'DAILY_PREDICT_TOP',
  'DAILY_LEVERAGE_TOP',
  'DAILY_TAP_GRID_TOP',
];

/** XP action → 该 action 依赖的阶梯任务 slug（用于在输入框旁展示当前最高档阈值） */
const XP_ACTION_TIER_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(TIER_SLUG_TOP_XP_ACTION).map(([slug, action]) => [action, slug])
);
type XpRuleMeta = {
  label: string;
  unit: string;
};

function getTopTierDesc(tasks: BbTask[], slug: string): string | null {
  const task = tasks.find((x) => x.slug === slug);
  const top = (task?.ladders || []).find((l) => !!l.isTopTier);
  if (!task || !top) return null;
  const threshold = top.volumeThreshold ?? '';
  const box = top.rewardBox ?? '';
  return `${threshold}`;
}

function getXpRuleMeta(rule: BbXpRule, t: (key: string) => string): XpRuleMeta {
  const m: Record<string, { labelKey: string; unitKey: string }> = {
    DAILY_LOGIN: {
      labelKey: 'module.blindBox.xpAction_daily_login',
      unitKey: 'module.blindBox.xpUnit_xp',
    },
    TRADE: {
      labelKey: 'module.blindBox.xpAction_trade',
      unitKey: 'module.blindBox.xpUnit_xp',
    },
    BOX_OPEN: {
      labelKey: 'module.blindBox.xpAction_box_open',
      unitKey: 'module.blindBox.xpUnit_xp',
    },
    INVITE: {
      labelKey: 'module.blindBox.xpAction_invite',
      unitKey: 'module.blindBox.xpUnit_xp_per_person',
    },
    SOCIAL_FOLLOW: {
      labelKey: 'module.blindBox.xpAction_social_follow',
      unitKey: 'module.blindBox.xpUnit_xp_per_item',
    },
    DAILY_PERP_TOP: {
      labelKey: 'module.blindBox.xpAction_daily_perp_top',
      unitKey: 'module.blindBox.xpUnit_xp_top_tier',
    },
    DAILY_PREDICT_TOP: {
      labelKey: 'module.blindBox.xpAction_daily_predict_top',
      unitKey: 'module.blindBox.xpUnit_xp_top_tier',
    },
    DAILY_LEVERAGE_TOP: {
      labelKey: 'module.blindBox.xpAction_daily_leverage_top',
      unitKey: 'module.blindBox.xpUnit_xp_top_tier',
    },
    DAILY_TAP_GRID_TOP: {
      labelKey: 'module.blindBox.xpAction_daily_tap_grid_top',
      unitKey: 'module.blindBox.xpUnit_xp_top_tier',
    },
  };

  const def = m[rule.action];
  return {
    label: def ? t(def.labelKey) : rule.description || rule.action,
    unit: def ? t(def.unitKey) : t('module.blindBox.xpUnit_xp'),
  };
}

export function SectionTasksGeneral({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionData>();
  const permissionList = permissions?.permissions;
  // PUT /blind-box/tasks/:id requires bb-tasks:update OR bb-trading-tiers:update
  const canUpdateTask =
    hasPermission(permissionList, 'bb-tasks', 'update') ||
    hasPermission(permissionList, 'bb-trading-tiers', 'update');
  // POST /blind-box/xp-rules requires bb-tasks:update OR bb-level-system:update
  const canUpsertXpRule =
    hasPermission(permissionList, 'bb-tasks', 'update') ||
    hasPermission(permissionList, 'bb-level-system', 'update');
  const [tasks, setTasks] = useState<BbTask[]>([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState<Partial<BbTask>>({});
  const [xpRules, setXpRules] = useState<BbXpRule[]>([]);
  const [xpValues, setXpValues] = useState<Record<string, string>>({});

  // const xpOrder = useMemo(
  //   () => ['DAILY_LOGIN', 'TRADE', 'BOX_OPEN', 'INVITE', 'SOCIAL_FOLLOW', 'DAILY_PERP_TOP'],
  //   []
  // );
  const xpFilterAndOrder = useMemo(() => XP_FILTER_AND_ORDER, []);
  const xpRulesSorted = useMemo(() => {
    const orderMap = new Map(xpFilterAndOrder.map((key, idx) => [key, idx]));

    // 先过滤：只保留在 xpFilterAndOrder 里的 action
    const filtered = xpRules.filter((item) => xpFilterAndOrder.includes(item.action));

    // 再排序：按指定顺序，不在列表里的排最后（权重999）
    return filtered.sort((a, b) => {
      const orderA = orderMap.get(a.action) ?? 999;
      const orderB = orderMap.get(b.action) ?? 999;
      return orderA - orderB;
    });
  }, [xpRules, xpFilterAndOrder]);

  const load = useCallback(() => {
    bbGetTasks()
      .then(setTasks)
      .catch(() => setTasks([]));
    Promise.all([bbGetXpRules()]).then(([x]) => {
      // 后端若暂未返回某些 action，这里补齐以便前端可配置并保存（例如 DAILY_PERP_TOP / DAILY_PREDICT_TOP）
      const byAction = new Map(x.map((r) => [r.action, r]));
      const ensured: BbXpRule[] = [
        ...x,
        ...xpFilterAndOrder
          .filter((action) => !byAction.has(action))
          .map((action) => ({
            action,
            xpAmount: 0,
          })),
      ];
      setXpRules(ensured);
    });
  }, [xpFilterAndOrder]);

  const saveXp = async (rule: BbXpRule, xp: number) => {
    try {
      await bbUpsertXpRule({ action: rule.action, xpAmount: xp });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  useEffect(() => {
    load();
  }, [load, refreshKey]);
  /** XP action → 对应阶梯任务的最高档阈值，用于输入框标题旁的提示 */
  const topTierDescByXpAction = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(XP_ACTION_TIER_SLUG).map(([action, slug]) => [
          action,
          getTopTierDesc(tasks, slug),
        ])
      ),
    [tasks]
  );

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) => (a.ladders?.length ? 1 : 0) - (b.ladders?.length ? 1 : 0)
      ),
    [tasks]
  );

  const save = async () => {
    try {
      if (form.id) {
        await bbUpdateTask(form.id, {
          name: form.name,
          description: form.description,
          rewardXp: form.rewardXp,
          rewardSpin: form.rewardSpin,
          rewardBox: form.rewardBox,
          isActive: form.isActive,
          displayOrder: form.displayOrder,
          externalLink: form.externalLink,
        });
      } else {
        await bbCreateTask({
          slug: form.slug!,
          name: form.name!,
          description: form.description,
          rewardXp: form.rewardXp!,
          rewardSpin: form.rewardSpin!,
          rewardBox: form.rewardBox!,
          isActive: form.isActive!,
          displayOrder: form.displayOrder!,
          externalLink: form.externalLink,
        });
      }
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setDlg(false);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const toggleActive = async (row: BbTask, next: boolean) => {
    try {
      await bbUpdateTask(row.id, { isActive: next });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  return (
    <Box>
      <PermissionGuard anyOf={TASK_CREATE_PERMS} fallback={null} loadingFallback={null}>
        <Button
          sx={{ mb: 2 }}
          startIcon={<AddIcon />}
          onClick={() => {
            setForm({
              slug: 'CUSTOM_TASK',
              name: '',
              rewardXp: 0,
              rewardSpin: 0,
              rewardBox: 0,
              isActive: true,
              displayOrder: tasks.length,
            });
            setDlg(true);
          }}
        >
          {t('module.blindBox.addTask')}
        </Button>
      </PermissionGuard>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>slug</TableCell>
              <TableCell>{t('module.blindBox.colName')}</TableCell>
              <TableCell>spin</TableCell>
              <TableCell>box</TableCell>
              <TableCell>xp</TableCell>
              <TableCell>{t('module.blindBox.displayOrder')}</TableCell>
              <TableCell>{t('module.blindBox.colActive')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTasks.map((x) => (
              <TableRow key={x.id}>
                <TableCell>
                  {x.slug && TASK_SLUG_I18N[x.slug]
                    ? t(TASK_SLUG_I18N[x.slug], { _: x.name || x.slug })
                    : x.name}
                </TableCell>
                <TableCell>{x.name}</TableCell>
                <TableCell>{x.ladders.length !== 0 ? '-' : x.rewardSpin}</TableCell>
                <TableCell>{x.ladders.length !== 0 ? '-' : x.rewardBox}</TableCell>
                <TableCell>{x.ladders.length !== 0 ? '-' : x.rewardXp}</TableCell>
                <TableCell>{x.displayOrder}</TableCell>
                <TableCell>
                  <Switch
                    checked={!!x.isActive}
                    disabled={!canUpdateTask}
                    onChange={(e) => toggleActive(x, e.target.checked)}
                  />
                </TableCell>
                <TableCell align="right">
                  <PermissionGuard anyOf={TASK_UPDATE_PERMS} fallback={null} loadingFallback={null}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setForm(x);
                        setDlg(true);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </PermissionGuard>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1">{t('module.blindBox.xpRules')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
          {t('module.blindBox.xpRulesHint')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          }}
        >
          {xpRulesSorted.map((r) => {
            const meta = getXpRuleMeta(r, t);
            const current = xpValues[r.action] ?? String(r.xpAmount ?? 0);
            const topTierSourceDesc = topTierDescByXpAction[r.action] ?? null;
            return (
              <Box key={r.action} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  {meta.label}
                  {topTierSourceDesc ? (
                    <Typography
                      variant="caption"
                      sx={{
                        // 关键：不要 block，用 inline / inline-block
                        display: 'inline-block',
                        ml: 0.5, // 左边间距（和 gap 二选一即可）
                      }}
                    >
                      {topTierSourceDesc}
                    </Typography>
                  ) : null}
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={current}
                  disabled={!canUpsertXpRule}
                  onChange={(e) =>
                    setXpValues((p) => ({
                      ...p,
                      [r.action]: e.target.value,
                    }))
                  }
                  onBlur={() => {
                    const next = parseInt((xpValues[r.action] ?? current) || '0', 10) || 0;
                    if (next !== (r.xpAmount ?? 0)) saveXp(r, next);
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {meta.unit}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {form.id ? t('module.blindBox.editTask') : t('module.blindBox.addTask')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!form.id && (
              // 后端按 slug 推导 category / resetType，已知 slug 走下拉，其余仍可自定义
              <Autocomplete
                freeSolo
                options={KNOWN_TASK_SLUGS}
                getOptionLabel={(opt) => String(opt)}
                renderOption={(props, opt) => (
                  <li {...props} key={opt}>
                    {opt}
                    {TASK_SLUG_I18N[opt] ? ` — ${t(TASK_SLUG_I18N[opt], { _: opt })}` : ''}
                  </li>
                )}
                value={form.slug || ''}
                onChange={(_e, v) => setForm({ ...form, slug: String(v ?? '') })}
                onInputChange={(_e, v) => setForm({ ...form, slug: v })}
                renderInput={(params) => <TextField {...params} label="slug" fullWidth />}
              />
            )}
            <TextField
              label={t('module.blindBox.colName')}
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            {form?.ladders?.length === 0 && (
              <>
                <TextField
                  type="number"
                  label="rewardSpin"
                  value={form.rewardSpin ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, rewardSpin: parseInt(e.target.value, 10) || 0 })
                  }
                  fullWidth
                />
                <TextField
                  type="number"
                  label="rewardBox"
                  value={form.rewardBox ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, rewardBox: parseInt(e.target.value, 10) || 0 })
                  }
                  fullWidth
                />
                <TextField
                  type="number"
                  label="rewardXp"
                  value={form.rewardXp ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, rewardXp: parseInt(e.target.value, 10) || 0 })
                  }
                  fullWidth
                />
              </>
            )}

            <TextField
              type="number"
              label="displayOrder"
              value={form.displayOrder ?? 0}
              onChange={(e) =>
                setForm({ ...form, displayOrder: parseInt(e.target.value, 10) || 0 })
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label={t('module.blindBox.colActive')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>{t('actions.cancel')}</Button>
          <PermissionGuard
            anyOf={form.id ? TASK_UPDATE_PERMS : TASK_CREATE_PERMS}
            fallback={null}
            loadingFallback={null}
          >
            <Button variant="contained" onClick={save}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
